import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import PropTypes from 'prop-types';
import React, { useMemo, useState, useCallback } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import Icon from '../../../common/containers/icon';
import { getAuditTrailRestorableEntries, formatAuditCreated, getAuditEntryPrimaryValue, normalizeSuggestionOptionLabel } from './tvcIssuesSectionHelpers';
import { commitAuditTrailRestore, commitSuggestionFix } from './tvcIssuesAuditRestore';

/**
 * Fix tab content: suggestion cards with Fix buttons, audit restorable values, or disclaimer.
 */
const TvcIssuesFixView = _ref => {
  var _item$rowData, _fixModal$spec$descri, _fixModal$spec;
  let item = _ref.item,
    stableRowKey = _ref.stableRowKey,
    onRestoreSucceeded = _ref.onRestoreSucceeded;
  const dispatch = useDispatch();
  const user = useSelector(state => {
    var _state$login;
    return (_state$login = state.login) === null || _state$login === void 0 ? void 0 : _state$login.user;
  });
  const geolocation = useSelector(state => {
    var _state$login2;
    return (_state$login2 = state.login) === null || _state$login2 === void 0 ? void 0 : _state$login2.geolocation;
  });
  const rowData = (_item$rowData = item === null || item === void 0 ? void 0 : item.rowData) !== null && _item$rowData !== void 0 ? _item$rowData : null;
  const suggestions = rowData === null || rowData === void 0 ? void 0 : rowData.suggestions;
  const auditTrail = rowData === null || rowData === void 0 ? void 0 : rowData.auditTrail;
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    restoreModal = _useState2[0],
    setRestoreModal = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    restoreBusy = _useState4[0],
    setRestoreBusy = _useState4[1];
  const _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    fixModal = _useState6[0],
    setFixModal = _useState6[1];
  const _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    suggestionBusyKey = _useState8[0],
    setSuggestionBusyKey = _useState8[1];
  const userFullName = useMemo(() => {
    if (!user) return '';
    const firstName = user.firstName,
      lastName = user.lastName;
    return "".concat(firstName !== null && firstName !== void 0 ? firstName : '', " ").concat(lastName !== null && lastName !== void 0 ? lastName : '').trim();
  }, [user]);
  const suggestionEntries = useMemo(() => {
    if (!suggestions || typeof suggestions !== 'object') return [];
    return Object.entries(suggestions).filter(_ref2 => {
      let _ref3 = _slicedToArray(_ref2, 2),
        spec = _ref3[1];
      return Array.isArray(spec === null || spec === void 0 ? void 0 : spec.options) && spec.options.length > 0;
    });
  }, [suggestions]);
  const hasAuditTrail = auditTrail != null;
  const hasSuggestionCards = suggestionEntries.length > 0;
  const showDisclaimer = !hasSuggestionCards && !hasAuditTrail;
  const restorableAuditEntries = useMemo(() => hasAuditTrail ? getAuditTrailRestorableEntries(auditTrail) : [], [auditTrail, hasAuditTrail]);
  const closeRestoreModal = useCallback(() => {
    setRestoreModal(null);
  }, []);
  const closeFixModal = useCallback(() => {
    setFixModal(null);
  }, []);
  const confirmFix = useCallback(async () => {
    if (!fixModal || suggestionBusyKey != null || restoreBusy) return;
    const typeKey = fixModal.typeKey,
      spec = fixModal.spec,
      rawOption = fixModal.rawOption,
      optionRowKey = fixModal.optionRowKey;
    setSuggestionBusyKey(optionRowKey);
    try {
      const lat = geolocation === null || geolocation === void 0 ? void 0 : geolocation.latitude;
      const lon = geolocation === null || geolocation === void 0 ? void 0 : geolocation.longitude;
      const ok = await commitSuggestionFix(dispatch, item, typeKey, spec, rawOption, userFullName, lat, lon);
      if (ok) {
        onRestoreSucceeded(stableRowKey);
        closeFixModal();
      }
    } finally {
      setSuggestionBusyKey(null);
    }
  }, [fixModal, suggestionBusyKey, restoreBusy, dispatch, item, userFullName, geolocation, onRestoreSucceeded, stableRowKey, closeFixModal]);
  const confirmRestore = useCallback(async () => {
    if (!(restoreModal !== null && restoreModal !== void 0 && restoreModal.entry) || restoreBusy) return;
    setRestoreBusy(true);
    try {
      const lat = geolocation === null || geolocation === void 0 ? void 0 : geolocation.latitude;
      const lon = geolocation === null || geolocation === void 0 ? void 0 : geolocation.longitude;
      const ok = await commitAuditTrailRestore(dispatch, item, restoreModal.entry, userFullName, lat, lon);
      if (ok) {
        onRestoreSucceeded(stableRowKey);
        closeRestoreModal();
      }
    } finally {
      setRestoreBusy(false);
    }
  }, [restoreModal, restoreBusy, dispatch, item, userFullName, geolocation, closeRestoreModal, onRestoreSucceeded, stableRowKey]);
  if (showDisclaimer) {
    return /*#__PURE__*/React.createElement("div", {
      className: "tvc-issues-section__fix-disclaimer text-muted small"
    }, "No fixes available.");
  }
  const restoreValueLabel = restoreModal ? getAuditEntryPrimaryValue(restoreModal.entry) : '';
  const fixValueLabel = fixModal ? normalizeSuggestionOptionLabel(fixModal.rawOption).trim() : '';
  const fixSuggestionDescription = (_fixModal$spec$descri = fixModal === null || fixModal === void 0 || (_fixModal$spec = fixModal.spec) === null || _fixModal$spec === void 0 || (_fixModal$spec = _fixModal$spec.description) === null || _fixModal$spec === void 0 ? void 0 : _fixModal$spec.trim()) !== null && _fixModal$spec$descri !== void 0 ? _fixModal$spec$descri : '';
  const fixOrRestorePending = fixModal != null || restoreModal != null;
  const suggestionFixDisabled = suggestionBusyKey != null || restoreBusy || fixOrRestorePending;
  return /*#__PURE__*/React.createElement("div", {
    className: "tvc-issues-section__fix-view"
  }, /*#__PURE__*/React.createElement(Modal, {
    isOpen: restoreModal != null,
    toggle: closeRestoreModal,
    centered: true,
    "aria-labelledby": "tvc-audit-restore-title",
    "aria-describedby": "tvc-audit-restore-body"
  }, /*#__PURE__*/React.createElement(ModalHeader, {
    toggle: closeRestoreModal,
    id: "tvc-audit-restore-title"
  }, "Confirm restore"), /*#__PURE__*/React.createElement(ModalBody, {
    id: "tvc-audit-restore-body"
  }, /*#__PURE__*/React.createElement("p", {
    className: "mb-3"
  }, "This will restore the previously entered value. Continue?"), restoreValueLabel ? /*#__PURE__*/React.createElement("p", {
    className: "mb-0"
  }, "Value:", ' ', /*#__PURE__*/React.createElement("b", null, restoreValueLabel)) : null), /*#__PURE__*/React.createElement(ModalFooter, null, /*#__PURE__*/React.createElement(Button, {
    color: "secondary",
    type: "button",
    onClick: closeRestoreModal,
    disabled: restoreBusy
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    type: "button",
    onClick: confirmRestore,
    disabled: restoreBusy
  }, restoreBusy ? 'Restoring…' : 'Yes, restore'))), /*#__PURE__*/React.createElement(Modal, {
    isOpen: fixModal != null,
    toggle: closeFixModal,
    centered: true,
    "aria-labelledby": "tvc-suggestion-fix-title",
    "aria-describedby": "tvc-suggestion-fix-body"
  }, /*#__PURE__*/React.createElement(ModalHeader, {
    toggle: closeFixModal,
    id: "tvc-suggestion-fix-title"
  }, "Confirm fix"), /*#__PURE__*/React.createElement(ModalBody, {
    id: "tvc-suggestion-fix-body"
  }, /*#__PURE__*/React.createElement("p", {
    className: "mb-3"
  }, "This will apply the suggested value to the form and add an audit trail entry.", ' ', "Continue?"), fixSuggestionDescription ? /*#__PURE__*/React.createElement("p", {
    className: "mb-2 text-muted small"
  }, fixSuggestionDescription) : null, fixValueLabel ? /*#__PURE__*/React.createElement("p", {
    className: "mb-0"
  }, "Value:", ' ', /*#__PURE__*/React.createElement("b", null, fixValueLabel)) : null), /*#__PURE__*/React.createElement(ModalFooter, null, /*#__PURE__*/React.createElement(Button, {
    color: "secondary",
    type: "button",
    onClick: closeFixModal,
    disabled: suggestionBusyKey != null
  }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    type: "button",
    onClick: confirmFix,
    disabled: suggestionBusyKey != null
  }, suggestionBusyKey != null ? 'Applying…' : 'Yes, apply fix'))), hasSuggestionCards && /*#__PURE__*/React.createElement("div", {
    className: "tvc-issues-section__fix-suggestions"
  }, suggestionEntries.map(_ref4 => {
    let _ref5 = _slicedToArray(_ref4, 2),
      typeKey = _ref5[0],
      spec = _ref5[1];
    return /*#__PURE__*/React.createElement("div", {
      key: typeKey,
      className: "tvc-issues-section__suggestion-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "tvc-issues-section__suggestion-card-header"
    }, /*#__PURE__*/React.createElement(Icon, {
      glyph: "lightbulb",
      weight: "far",
      className: "tvc-issues-section__suggestion-icon"
    }), /*#__PURE__*/React.createElement("div", {
      className: "tvc-issues-section__suggestion-card-title-wrap"
    }, spec.description ? /*#__PURE__*/React.createElement("div", {
      className: "tvc-issues-section__suggestion-description-title"
    }, spec.description) : null)), /*#__PURE__*/React.createElement("ul", {
      className: "tvc-issues-section__suggestion-options"
    }, spec.options.map((opt, optIndex) => {
      const optionRowKey = "".concat(typeKey, "-").concat(optIndex);
      const label = normalizeSuggestionOptionLabel(opt);
      const fixDisabled = suggestionFixDisabled;
      return /*#__PURE__*/React.createElement("li", {
        // Options may repeat; index keeps keys unique for React.
        // eslint-disable-next-line react/no-array-index-key
        key: "".concat(typeKey, "-").concat(label, "-").concat(optIndex),
        className: "tvc-issues-section__suggestion-option-row"
      }, /*#__PURE__*/React.createElement("div", {
        className: "tvc-issues-section__fix-value-block tvc-issues-section__fix-value-block--suggestion"
      }, /*#__PURE__*/React.createElement("span", {
        className: "tvc-issues-section__fix-value-label"
      }, "Value:"), /*#__PURE__*/React.createElement("span", {
        className: "tvc-issues-section__fix-value-text"
      }, label || '—')), /*#__PURE__*/React.createElement(Button, {
        color: "secondary",
        size: "sm",
        className: "tvc-issues-section__suggestion-fix-btn",
        disabled: fixDisabled,
        onClick: e => {
          e.stopPropagation();
          setFixModal({
            typeKey,
            spec,
            rawOption: opt,
            optionRowKey
          });
        }
      }, suggestionBusyKey === optionRowKey ? 'Applying…' : 'Fix'));
    })));
  })), hasAuditTrail && /*#__PURE__*/React.createElement("div", {
    className: "tvc-issues-section__audit-visual"
  }, /*#__PURE__*/React.createElement("div", {
    className: "tvc-issues-section__audit-visual-label small text-muted"
  }, "Audit Trail - Previous Values"), restorableAuditEntries.length === 0 ? /*#__PURE__*/React.createElement("div", {
    className: "tvc-issues-section__audit-visual-empty text-muted small"
  }, "No saved values in the audit trail to restore.") : /*#__PURE__*/React.createElement("ul", {
    className: "tvc-issues-section__audit-entry-list"
  }, restorableAuditEntries.map(_ref6 => {
    var _entry$user;
    let entryId = _ref6.entryId,
      entry = _ref6.entry;
    // eslint-disable-next-line no-underscore-dangle
    const createdAt = entry._created;
    return /*#__PURE__*/React.createElement("li", {
      key: entryId,
      className: "tvc-issues-section__audit-entry-row"
    }, /*#__PURE__*/React.createElement("div", {
      className: "tvc-issues-section__audit-entry-main"
    }, /*#__PURE__*/React.createElement("div", {
      className: "tvc-issues-section__fix-value-block tvc-issues-section__fix-value-block--audit"
    }, /*#__PURE__*/React.createElement("span", {
      className: "tvc-issues-section__fix-value-label"
    }, "Value:"), /*#__PURE__*/React.createElement("span", {
      className: "tvc-issues-section__fix-value-text"
    }, getAuditEntryPrimaryValue(entry))), /*#__PURE__*/React.createElement("div", {
      className: "tvc-issues-section__audit-entry-meta"
    }, /*#__PURE__*/React.createElement("span", null, (_entry$user = entry.user) !== null && _entry$user !== void 0 ? _entry$user : '—'), /*#__PURE__*/React.createElement("span", {
      className: "tvc-issues-section__audit-entry-sep"
    }, "\xB7"), /*#__PURE__*/React.createElement("span", null, formatAuditCreated(createdAt)))), /*#__PURE__*/React.createElement(Button, {
      color: "secondary",
      size: "sm",
      className: "tvc-issues-section__audit-restore-btn",
      disabled: restoreBusy || fixModal != null,
      onClick: e => {
        e.stopPropagation();
        setRestoreModal({
          entryId,
          entry
        });
      }
    }, "Restore"));
  }))));
};
TvcIssuesFixView.propTypes = {
  stableRowKey: PropTypes.string.isRequired,
  onRestoreSucceeded: PropTypes.func.isRequired,
  item: PropTypes.shape({
    formID: PropTypes.string,
    formTypeIdentifier: PropTypes.string,
    errorType: PropTypes.string,
    rowData: PropTypes.shape({
      formID: PropTypes.string,
      suggestions: PropTypes.objectOf(PropTypes.shape({
        fieldType: PropTypes.oneOf(['string', 'number', 'option']),
        contentPath: PropTypes.string,
        path: PropTypes.string,
        optionKey: PropTypes.string,
        optionId: PropTypes.string,
        options: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.shape({
          title: PropTypes.string,
          value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          optionKey: PropTypes.string,
          optionId: PropTypes.string
        })])),
        description: PropTypes.string
      })),
      auditTrail: PropTypes.shape({
        id: PropTypes.string,
        auditTrail: PropTypes.objectOf(PropTypes.shape({})),
        auditTrailIdentificationSection: PropTypes.shape({
          formID: PropTypes.string
        })
      })
    })
  }).isRequired
};
export default TvcIssuesFixView;